<template>
    <div>
        <div class="mt-5 flex flex-col md:flex-row justify-arrow">
            <div class="flex flex-col items-center md:items-start w-full">
                <h1 class="text-3xl text-center md:text-left">
                    {{ id ? "" : "Nueva Bodega" }}
                    <strong v-if="id" class="text-theme-primary">
                        {{ itemComponent.name }}
                    </strong>
                </h1>
                <div v-if="id" class="flex">
                    <strong class="text-sm text-theme-muted">
                        Slug {{ itemComponent.slug }}
                    </strong>
                </div>
            </div>
            <div
                v-if="id"
                class="flex flex-col mt-2 md:mt-0 md:min-w-max justify-center text-center"
            >
                <small> 📆💾 {{ itemComponent.createdAt | date }} </small>
                <small> 📆✏ {{ itemComponent.updatedAt | date }} </small>
            </div>
        </div>

        <custom-navbar
            v-if="id"
            id="tabs-products"
            v-model="activeTab"
            center-collapsed
            not-line
        >
            <template #left>
                <custom-navbar-item id="home">
                    <router-link
                        to="/app/warehouses"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                </custom-navbar-item>

                <custom-navbar-item
                    id="general"
                    :active="['general', 'home'].includes(activeTab)"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> General </span>
                        <span>&nbsp;⚙</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('warehouse:GetMoney')"
                    id="money"
                    :active="activeTab === 'money'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Dinero </span>
                        <span>&nbsp;💰</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('warehouse:GetServices')"
                    id="services"
                    :active="activeTab === 'services'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Servicios </span>
                        <span>&nbsp;👨‍🏭</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('user:ListOwn')"
                    id="users"
                    :active="activeTab === 'users'"
                    class="px-2 md:px-4 xl:px-6"
                    @click="listMerchantsUserComponent"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Usuarios </span>
                        <span>&nbsp;👩‍🔧</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="
                        $ability.hasPermission('warehouse:Get') &&
                        $enabledEcommerceOptions &&
                        !isCreation
                    "
                    id="seo"
                    :active="activeTab === 'seo'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Contenido&nbsp; </span>
                        <span>&nbsp;📒</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    id="advance"
                    :active="activeTab === 'advance'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Avanzado&nbsp; </span>
                        <span>🤖</span>
                    </div>
                </custom-navbar-item>
            </template>
            <template #right>
                <custom-switch
                    v-if="$ability.hasPermission('warehouse:Update')"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </template>
        </custom-navbar>

        <div v-show="['general', 'home'].includes(activeTab)">
            <div v-if="id" class="flex flex-wrap">
                <div
                    class="w-full md:w-1/2 mt-8 flex justify-center items-center"
                >
                    <div
                        v-if="
                            $enabledEcommerceOptions &&
                            $ability.hasPermission('warehouse:UpdateActive')
                        "
                        class="flex items-center gap-2"
                    >
                        <span class="text-sm font-medium text-theme-base">
                            Habilitado para entregas
                        </span>
                        <custom-switch
                            v-if="enabledUpdate"
                            v-model="itemComponent.enabledForDeliveries"
                            success
                            @change="updateActiveComponent"
                        >
                            <template #on><small>Si</small></template>
                            <template #off><small>No</small></template>
                        </custom-switch>
                        <span
                            v-else
                            class="text-sm font-semibold"
                            :class="
                                itemComponent.enabledForDeliveries
                                    ? 'text-theme-success'
                                    : 'text-theme-danger'
                            "
                        >
                            {{
                                itemComponent.enabledForDeliveries ? "Si" : "No"
                            }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="flex flex-wrap gap-y-4">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName && introValidators.name
                                ? 'Nombre de bodega invalido'
                                : ''
                        "
                        @blur="setSlug"
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.slug"
                        label="Slug"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSlug && introValidators.slug
                                ? 'Slug invalido'
                                : ''
                        "
                        @blur="introValidators.slug = true"
                    />
                </div>
            </div>

            <div class="flex flex-wrap gap-y-4 mt-4">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.summary"
                        label="Descripción"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <city-code-selector
                        v-model="itemComponent.cityCode"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.address"
                        label="Dirección. Ej. Cll 42 # 12 - 8"
                        placeholder="Cll 42 # 12 - 8"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidAddress && introValidators.address
                                ? 'Dirección invalida'
                                : ''
                        "
                        @blur="onBlurAddress"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.addressComplement"
                        label="Complemento de Dirección. Ej bodega 12"
                        placeholder="Bodega 12"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.neighborhood"
                        label="Barrio / Localidad"
                        :disabled="!enabledUpdate"
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.phone"
                        label="Celular de contacto"
                        type="number"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidPhone && introValidators.phone
                                ? 'Celular invalido'
                                : ''
                        "
                        @blur="introValidators.phone = true"
                    />
                </div>
                <fragment v-if="$enabledEcommerceOptions">
                    <div class="w-full md:w-1/2 px-2">
                        <custom-input
                            v-model="itemComponent.telegramChannelId"
                            label="Id de canal de Telegram"
                            :disabled="!enabledUpdate"
                        />
                    </div>
                    <div class="w-full md:w-1/2 mt-6 px-2">
                        <coordinates-selector
                            v-model="itemComponent.coordinates"
                            :enabled="enabledUpdate"
                        />
                    </div>
                </fragment>
            </div>

            <div class="flex flex-wrap mt-4">
                <images-manage
                    :id="id"
                    v-model="images"
                    type="bodega"
                    :enabled="enabledUpdate"
                    :default-alt="'Imagen de ' + itemComponent.name"
                    default-size="squareImage"
                />
            </div>

            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button
                        v-if="!id"
                        size="large"
                        @click="createItemComponent"
                    >
                        Crear bodega
                    </app-button>
                    <app-button
                        v-else
                        size="large"
                        @click="updateItemComponent"
                    >
                        Actualizar bodega
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'seo'" id="seoTab" class="space-y-6">
            <div class="flex flex-col">
                <h2 class="text-theme-primary text-2xl font-semibold my-4">
                    Contenido de la bodega
                </h2>
                <span>
                    Este contenido es el que se va a mostrar en la página de la
                    bodega, es importante que esté bien redactado y sea
                    atractivo para los usuarios.
                </span>
            </div>

            <div
                v-if="$enabledEcommerceOptions"
                class="bg-theme-card rounded-2xl shadow-sm p-5 mt-5"
            >
                <h2 class="text-theme-primary text-xl font-semibold mb-4">
                    Contenido
                </h2>
                <markdown-editor
                    v-model="itemComponent.content"
                    class="mt-2"
                    :enabled="enabledUpdate"
                />
            </div>

            <div
                v-if="$enabledEcommerceOptions"
                class="bg-theme-card rounded-2xl shadow-sm p-5"
            >
                <h2 class="text-theme-primary text-xl font-semibold mb-4">
                    Palabras clave
                </h2>
                <keywords-selector
                    v-model="itemComponent.keywords"
                    :enabled="enabledUpdate"
                />
            </div>

            <div v-if="enabledUpdate" class="w-full flex justify-center">
                <app-button
                    :loading="isLoadingContent"
                    @click="updateContentComponent"
                >
                    Actualizar Contenido
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'money'">
            <div class="flex mt-4">
                en esta sección puedes actualizar el dinero que esta en la
                caja.Ten cuidado de no hacerlo en un horario donde haya mucho
                flujo de trasacciones ya que puedes eliminar el dinero ingresado
                por una trasacción accidentalmente.
            </div>
            <money-config-manage
                v-model="itemComponent.moneyConfig"
                :enabled="
                    $ability.hasPermission('warehouse:UpdateMoney') &&
                    enabledUpdate
                "
            />
            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button @click="updateMerchantMoneyComponent">
                        Actualizar dinero en bodega
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'services'">
            <services-in-merchant-manage
                v-model="itemComponent.services"
                :enabled="
                    $ability.hasPermission('warehouse:UpdateServices') &&
                    enabledUpdate
                "
                class="mt-4"
            />
            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button @click="updateServicesMerchantComponent">
                        Actualizar servicios
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'users'">
            <div v-if="isLoadingUsers" class="w-full flex justify-center my-4">
                <span>Cargando...</span>
            </div>
            <div v-else>
                <users-roles-manage
                    v-model="users"
                    :enabled="
                        $ability.hasPermission('user:UpdateOwn') &&
                        enabledUpdate
                    "
                    :default-filters="{ type: 'merchant' }"
                />
                <div class="flex flex-wrap mt-4">
                    <div
                        v-if="enabledUpdate"
                        class="w-full flex justify-center"
                    >
                        <app-button @click="updateUsersMerchantComponent">
                            Actualizar usuarios
                        </app-button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'advance'" id="advanceTab">
            <div id="title-routes" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl">Avanzado</h2>
                    <span>
                        en esta sección prodrás activar o desactivar inventario,
                        sincronizar precios o eliminar la bodega aqui, ten mucho
                        cuidado.
                    </span>
                </div>
            </div>
            <div
                v-if="id"
                class="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
                <app-button
                    v-if="enabledUpdate"
                    @click="selectMerchantComponent"
                >
                    Selecciona bodega para operaciones
                </app-button>
                <app-button
                    v-if="
                        $enabledEcommerceOptions &&
                        $ability.hasPermission('warehouse:Transfer')
                    "
                    @click="transferComponent"
                >
                    Establecer como
                    {{ itemComponent.isOwn ? "proveedor" : "sucursal" }}
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('inventory:SyncPrices')"
                    @click="syncPricesInventoryComponent"
                >
                    Sincronizar precios con {{ $siteType }}
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('inventory:Active')"
                    @click="activeInventoryComponent"
                >
                    Activar todo el inventario
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('inventory:Active')"
                    warn
                    @click="disableInventoryComponent"
                >
                    Desactivar todo el inventario
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('warehouse:Delete')"
                    danger
                    @click="deleteItemComponent"
                >
                    Eliminación
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('warehouse:DeleteDefinitive')"
                    danger
                    @click="deleteItemDefinitelyComponent"
                >
                    Eliminación definitiva
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters, mapMutations } from "vuex";

import { DEFAULT_FRANCHISE_MARGIN } from "@/common/constants";
import { EMPTY_MONEY_CONFIG } from "@/common/lib/money";
import {
    addressRegex,
    nameRegex,
    phoneRegex,
    slugRegex
} from "@/common/lib/regex";
import {
    removeEmptyFields,
    slugify,
    validateConfirmationMessage
} from "@/common/lib/transformers";

import UsersRolesManage from "@/components/users/UsersRolesManage.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CoordinatesSelector from "@/components/utils/CoordinatesSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import KeywordsSelector from "@/components/utils/KeywordsSelector.vue";
import MarkdownEditor from "@/components/utils/MarkdownEditor.vue";
import MoneyConfigManage from "@/components/utils/MoneyConfigManage.vue";
import ServicesInMerchantManage from "@/components/utils/ServicesInMerchantManage.vue";

export default {
    name: "WarehouseDetail",
    components: {
        ImagesManage,
        MarkdownEditor,
        CoordinatesSelector,
        CityCodeSelector,
        CustomInput,
        MoneyConfigManage,
        UsersRolesManage,
        ServicesInMerchantManage,
        KeywordsSelector
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        activeTab: "general",
        $tabsWhitelist: [
            "general",
            "money",
            "services",
            "users",
            "seo",
            "advance"
        ],
        id: "",
        isLoading: false,
        isLoadingContent: false,
        enabledUpdate: false,
        loadedAllData: false,
        isLoadingUsers: false,
        users: [],
        images: [],
        itemComponent: {
            id: "",
            name: "",
            slug: "",
            content: "",
            enabledForDeliveries: false,
            summary: "",
            phone: "",
            coordinates: { latitude: 4.63082, longitude: -74.15035 },
            moneyConfig: EMPTY_MONEY_CONFIG,
            address: "",
            cityCode: "11001",
            neighborhood: "",
            addressComplement: "",
            weight: 120,
            telegramChannelId: "",
            images: []
        },
        introValidators: {
            phone: false,
            address: false,
            name: false,
            slug: false,
            telegramChannelId: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidAddress() {
            return (
                !this.itemComponent.address ||
                addressRegex.test(this.itemComponent.address)
            );
        },
        isValidPhone() {
            return (
                !this.itemComponent.phone ||
                phoneRegex.test(this.itemComponent.phone)
            );
        },
        isValidName() {
            return nameRegex.test(this.itemComponent.name);
        },
        isValidSlug() {
            return slugRegex.test(this.itemComponent.slug);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (
                !(await verifyPagePermission(
                    "warehouse:Get",
                    "/app/warehouses"
                ))
            )
                return;
            const id = this.$route.params.id;
            this.id = id;
            await this.getBaseItem(id);
        } else if ("idClone" in this.$route.params) {
            if (
                !(await verifyPagePermission(
                    "warehouse:Create",
                    "/app/warehouses"
                ))
            )
                return;
            this.id = 0;
            this.enabledUpdate = true;
            const idTmp = this.$route.params.idClone;
            await this.getBaseItem(idTmp);
            this.itemComponent = {
                ...this.itemComponent,
                name: `${this.itemComponent.name} CLONE`,
                slug: `${this.itemComponent.slug}-clone`,
                enabledForDeliveries: false
            };
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas clonando una bodega",
                icon: "info",
                showCloseButton: true
            });
        } else {
            if (
                !(await verifyPagePermission(
                    "warehouse:Create",
                    "/app/warehouses"
                ))
            )
                return;
            this.enabledUpdate = true;
            this.loadedAllData = true;
        }
    },
    methods: {
        ...mapMutations("commons", ["SET_MERCHANT_SELECTED"]),
        ...mapActions("auth", ["applyMerchantPermissions"]),
        ...mapActions("warehouses", [
            "getWarehouseById",
            "updateWarehouse",
            "deleteWarehouse",
            "deleteWarehouseDefinitively",
            "createWarehouse",
            "updateWarehouseMoney",
            "updateWarehouseServices",
            "setWarehouseActive",
            "transferWarehouse"
        ]),
        ...mapActions("inventories", [
            "syncPricesInventory",
            "activeAllInventories",
            "disableAllInventories"
        ]),
        ...mapActions("users", ["listUsersMerchant", "updateUsersMerchant"]),
        async getBaseItem(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.getWarehouseById({ id });
                this.itemComponent = {
                    ...data,
                    ...data.communicationConfig,
                    services: data.services.map((item) => ({
                        priceOffer: item.priceOffer,
                        name: item.product.name
                    }))
                };
                this.images = [...this.itemComponent.images];
                this.loadedAllData = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/warehouses");
                }
            }
        },
        async listMerchantsUserComponent() {
            this.isLoadingUsers = true;
            this.$loading.show();
            try {
                this.users = await this.listUsersMerchant({
                    merchantId: this.id,
                    merchantType: "warehouse"
                });
                this.isLoadingUsers = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoadingUsers = false;
                this.$loading.hide();
                this.isLoadingUsers = false;
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateActiveComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.setWarehouseActive(this.itemComponent);
                this.$sound.play();
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.itemComponent.enabledForDeliveries =
                    !this.itemComponent.enabledForDeliveries;
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateWarehouse(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createWarehouse(this.getDataSend());
                this.$sound.play();
                this.$loading.hide();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Id: ${data.id}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de bodegas",
                    cancelButtonText: "Nueva bodega"
                });
                if (dialogResult.isConfirmed) {
                    this.id = data.id;
                    this.itemComponent = { ...data, ...this.itemComponent };
                    this.$router.push(`/app/warehouses/detail/${this.id}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/warehouses/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        phone: false,
                        address: false,
                        name: false,
                        slug: false
                    };
                    this.itemComponent = {
                        id: "",
                        name: "",
                        slug: "",
                        enabledForDeliveries: false,
                        content: "",
                        summary: "",
                        phone: "",
                        coordinates: {
                            latitude: 4.63082,
                            longitude: -74.15035
                        },
                        moneyConfig: EMPTY_MONEY_CONFIG,
                        address: "",
                        cityCode: "11001",
                        neighborhood: "",
                        addressComplement: "",
                        weight: 120
                    };
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/warehouses");
                }
            }
        },
        async updateMerchantMoneyComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateWarehouseMoney({
                    id: this.itemComponent.id,
                    moneyConfig: this.itemComponent.moneyConfig
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateServicesMerchantComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateWarehouseServices({
                    id: this.itemComponent.id,
                    services: this.itemComponent.services.map((item) => ({
                        appliedDiscountPerReference: 0,
                        priceOffer: item.priceOffer,
                        product: {
                            name: item.name
                        },
                        quantity: 1,
                        id: 0
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteItemComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas seguro de eliminar la bodega ?",
                text: "Las bodegas solo deben eliminarse en caso de que se esten haciendo pruebas, eres consiente de lo que estas haciendo ?. Escribe 'Si' para eliminar",
                icon: "warning",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteWarehouse({
                        id: this.id
                    });
                    this.$router.push("/app");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async deleteItemDefinitelyComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas seguro de eliminar la bodega ?",
                text: `Las bodegas solo deben eliminarse en caso de que se esten haciendo pruebas, eres consiente de lo que estas haciendo ?. Escribe 'Si' para eliminar`,
                icon: "warning",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteWarehouseDefinitively({
                        id: this.id
                    });
                    this.$router.push("/app");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async syncPricesInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a sincronizar los precios de tu inventario ?",
                text: `Quieres que TODOS los precios del tienda${this.itemComponent.name} tendrá el mismo precio de la referencia en el ${this.$siteType}. Para reestablecer un precio debes entrar el detalle de la referencia.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.syncPricesInventory({
                        merchantType: "warehouse",
                        merchantId: this.itemComponent.id
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async transferComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: this.itemComponent.isOwn
                    ? "Vas a marcar la bodega como proveedor ?"
                    : "Vas a marcar la bodega como sucursal ?",
                text: this.itemComponent.isOwn
                    ? `A partir de ahora cada vez que ingreses un producto al inventario no estará habilitado por defecto para asignación de ordenes recibidas por el ${this.$siteType}.`
                    : `A partir de ahora tendrás disponible cada producto que ingreses en el inventario para asignación de ordenes recibidas por el ${this.$siteType}.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.transferWarehouse({
                        id: this.itemComponent.id,
                        isOwn: !this.itemComponent.isOwn
                    });
                    this.itemComponent.isOwn = !this.itemComponent.isOwn;
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async activeInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a activar el inventario ?",
                text: `Quieres todos los productos en el inventario esten disponibles para ser comprados en el ${this.$siteType}. Recuerda que por defecto el precio al que se acuerda el paso de los productos del tiendaal deja un margen del ${DEFAULT_FRANCHISE_MARGIN}% para el ${this.$siteType}.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.activeAllInventories({
                        merchantType: "warehouse",
                        merchantId: this.itemComponent.id
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async disableInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a activar el inventario ?",
                text: `Quieres todos los productos en el inventario NO esten disponibles para ser comprados en el ${this.$siteType}.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.disableAllInventories({
                        merchantType: "warehouse",
                        merchantId: this.itemComponent.id
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async updateUsersMerchantComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateUsersMerchant({
                    merchantType: "warehouse",
                    merchantId: this.id,
                    users: this.users.map((item) => ({
                        email: item.email,
                        roleSlug: item.role.slug
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async setSlug() {
            this.introValidators.name = true;
            if (
                this.enabledUpdate &&
                this.isValidName &&
                !this.itemComponent.enabledForDeliveries
            ) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Quieres cambiar el slug ?",
                    icon: "info",
                    showCancelButton: true,
                    confirmButtonColor: "#02913f",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Si",
                    cancelButtonText: "No"
                });
                if (dialogResult.isConfirmed) {
                    this.itemComponent.slug = slugify(this.itemComponent.name);
                }
            }
        },
        selectMerchantComponent() {
            const selectMerchant = {
                name: this.itemComponent.name,
                address: this.itemComponent.address,
                merchantType: "warehouse",
                merchantId: this.itemComponent.id,
                moneyConfig: this.itemComponent.moneyConfig
            };
            this.SET_MERCHANT_SELECTED(selectMerchant);
            this.applyMerchantPermissions([]);
        },
        removeUser({ id }) {
            const index = this.users.findIndex((item) => item.id == id);
            let users = [...this.users];
            users.splice(index, 1);
            this.users = [...users];
        },
        validateData() {
            this.introValidators = {
                slug: true,
                phone: true,
                address: true,
                name: true,
                telegramChannelId: true
            };

            const toComplete = [];
            if (!this.isValidSlug) {
                toComplete.push("Slug");
            }

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (!this.isValidPhone) {
                toComplete.push("Celular");
            }

            if (!this.isValidAddress) {
                toComplete.push("Dirección");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Bodega invalida",
                    text: `Revisa por favor revisa los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        getDataSend() {
            return removeEmptyFields({
                ...this.itemComponent,
                communicationConfig: {
                    telegramChannelId: this.itemComponent.telegramChannelId
                },
                images: this.images.map((item) => ({
                    id: item.id,
                    sequence: item.sequence.sequence
                })),
                id: this.id
            });
        },
        async updateContentComponent() {
            this.isLoadingContent = true;
            this.$loading.show();
            try {
                await this.updateWarehouse({
                    id: this.id,
                    content: this.itemComponent.content
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoadingContent = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoadingContent = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onBlurAddress() {
            this.introValidators.address = true;
            this.itemComponent.address = this.itemComponent.address.trim();
        }
    }
};
</script>
